import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const INTERVAL_LABEL = { one_time: "", month: "/ month", year: "/ year" };

export default function PublicPayPage() {
  const { linkId } = useParams();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/payment-links/public/${linkId}`);
        setLink(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Link not found");
      } finally { setLoading(false); }
    })();
  }, [linkId]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/checkout/session", {
        payment_link_id: linkId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setPaying(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-secondary-fg">Loading…</div>;
  if (!link) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="font-display text-3xl">Link not found</div>
        <Link to="/" className="btn-ghost inline-block mt-6">Back to y ℶα</Link>
      </div>
    </div>
  );

  const accent = link.brand_color || "#D4AF37";
  const intervalLabel = INTERVAL_LABEL[link.interval || "one_time"] || "";

  return (
    <main data-testid="public-pay-page" className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-lg">
        <div className="text-center mb-12">
          {link.logo_url ? (
            <img data-testid="merchant-logo" src={link.logo_url} alt={link.merchant_name} className="h-10 mx-auto" onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <Link to="/" className="font-display text-3xl gold-text">y ℶα</Link>
          )}
        </div>
        <div className="lux-card p-12 text-center" style={{ borderTop: `2px solid ${accent}` }}>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-fg">Payment to</div>
          <div data-testid="merchant-name" className="font-display text-2xl mt-2" style={{ color: accent }}>{link.merchant_name || "Merchant"}</div>

          <div data-testid="pay-amount" className="font-display text-7xl lg:text-8xl mt-10 tracking-tighter font-light" style={{ color: accent }}>
            {formatMoney(link.amount, link.currency)}
          </div>
          {intervalLabel && (
            <div data-testid="pay-interval" className="text-sm uppercase tracking-widest text-muted-fg mt-2">{intervalLabel}</div>
          )}

          <div className="text-sm text-secondary-fg mt-6 font-medium">{link.title}</div>
          {link.description && <div className="text-xs text-muted-fg mt-2 max-w-sm mx-auto">{link.description}</div>}

          <button
            data-testid="pay-button"
            onClick={pay}
            disabled={paying}
            className="btn-gold w-full mt-12"
            style={{ background: `linear-gradient(180deg, ${accent}cc, ${accent})`, borderColor: `${accent}66`, color: "#0A0A0B" }}
          >
            {paying ? "Redirecting…" : `Pay ${formatMoney(link.amount, link.currency)}${intervalLabel}`}
          </button>
          <div className="mt-6 text-xs text-muted-fg flex items-center justify-center gap-2">
            <Lock size={12} /> Secured by Stripe · y ℶα Vault
          </div>
        </div>
      </div>
    </main>
  );
}
